import { useEffect, useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Plus, Download, PaperPlaneTilt, CheckCircle, Trash, Receipt as ReceiptIcon } from "@phosphor-icons/react";
import { api, formatUSD, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { CreateReceiptModal } from "@/components/CreateReceiptModal";

const METHOD_LABEL = {
  cash: "Cash", check: "Check", cashapp: "CashApp", zelle: "Zelle", venmo: "Venmo",
  wire: "Wire", ach: "ACH", paypal: "PayPal", other: "Other",
};

export default function AdminReceipts() {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [methodFilter, setMethodFilter] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/receipts${methodFilter ? `?method=${methodFilter}` : ""}`);
      setReceipts(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load receipts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [methodFilter]);

  const resend = async (id) => {
    try {
      await api.post(`/admin/receipts/${id}/send`);
      toast.success("Receipt re-sent");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed");
    }
  };

  const downloadPdf = async (id, number) => {
    try {
      const r = await api.get(`/admin/receipts/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (_) {
      toast.error("Download failed");
    }
  };

  const voidReceipt = async (id) => {
    if (!window.confirm("Void this receipt? It'll be marked void but preserved for your records.")) return;
    try {
      await api.delete(`/admin/receipts/${id}`);
      toast.success("Receipt voided");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap" data-testid="admin-receipts-header">
        <div>
          <h1 className="font-display text-3xl uppercase">Receipts</h1>
          <div className="text-xs text-[#a3a3a3] mt-1">Send branded receipts for payments received off-platform (cash, CashApp, Zelle, etc.).</div>
        </div>
        <div className="flex items-center gap-2">
          <select value={methodFilter} onChange={(e) => setMethodFilter(e.target.value)}
            data-testid="receipt-method-filter"
            className="bg-[#0a0a0a] border border-[#262626] px-3 py-2 text-xs uppercase tracking-wider focus:border-[#28C878] outline-none">
            <option value="">All methods</option>
            {Object.entries(METHOD_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <button onClick={() => setCreateOpen(true)} data-testid="new-receipt-btn"
            className="px-4 py-2 text-xs uppercase tracking-wider flex items-center gap-2 bg-[#28C878] text-black hover:bg-[#28C878]/90 transition-colors font-bold">
            <Plus size={14} weight="bold" /> New receipt
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-[#a3a3a3]">Loading…</div>
      ) : receipts.length === 0 ? (
        <div className="border border-[#262626] bg-[#0a0a0a] p-12 text-center" data-testid="receipts-empty">
          <ReceiptIcon size={32} className="text-[#28C878] mx-auto mb-3" weight="fill" />
          <div className="font-display text-xl uppercase">No receipts yet</div>
          <div className="text-xs text-[#a3a3a3] mt-2 max-w-md mx-auto">
            Click <strong>New receipt</strong> to send a branded PDF receipt for a cash / CashApp / Zelle payment you already received.
          </div>
        </div>
      ) : (
        <div className="border border-[#262626] divide-y divide-[#262626] bg-[#0a0a0a]" data-testid="receipts-list">
          {receipts.map((rc) => {
            const isVoid = rc.status === "void";
            return (
              <div key={rc.id} className={`p-4 flex flex-col md:flex-row md:items-center gap-3 ${isVoid ? "opacity-60" : ""}`} data-testid={`receipt-row-${rc.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    {isVoid ? (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider border border-[#262626] text-[#666] px-2 py-0.5">
                        <Trash size={10} /> Void
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider border border-[#28C878]/40 text-[#28C878] px-2 py-0.5">
                        <CheckCircle size={10} weight="fill" /> Sent
                      </span>
                    )}
                    <span className="text-[10px] uppercase tracking-wider text-[#666] font-mono">{rc.receipt_number}</span>
                    <span className="text-[10px] uppercase tracking-wider text-[#a3a3a3] border border-[#262626] px-2 py-0.5">
                      {METHOD_LABEL[rc.payment_method] || rc.payment_method}
                    </span>
                    {rc.reference_no && (
                      <span className="text-[10px] text-[#a3a3a3] font-mono">ref {rc.reference_no}</span>
                    )}
                  </div>
                  <div className="text-sm text-white">
                    {rc.client_name}{rc.client_company ? ` · ${rc.client_company}` : ""}
                  </div>
                  <div className="text-[11px] text-[#a3a3a3] mt-0.5">
                    Paid {rc.paid_at?.slice(0,10)}{rc.line_items?.length ? ` · ${rc.line_items.length} line${rc.line_items.length === 1 ? "" : "s"}` : ""}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-display text-xl tabular-nums text-[#28C878]">{formatUSD(rc.total_usd)}</div>
                </div>
                <div className="flex items-center gap-1 shrink-0 flex-wrap justify-end">
                  {!isVoid && (
                    <button onClick={() => resend(rc.id)} data-testid={`receipt-resend-${rc.id}`}
                      className="text-[10px] uppercase tracking-wider border border-[#28C878]/40 hover:border-[#28C878] hover:text-white text-[#28C878] px-2.5 py-1.5 transition-colors flex items-center gap-1">
                      <PaperPlaneTilt size={10} weight="bold" /> Resend
                    </button>
                  )}
                  <button onClick={() => downloadPdf(rc.id, rc.receipt_number)} data-testid={`receipt-pdf-${rc.id}`}
                    className="text-[10px] uppercase tracking-wider border border-[#262626] hover:border-[#28C878] hover:text-white text-[#a3a3a3] px-2.5 py-1.5 transition-colors flex items-center gap-1">
                    <Download size={10} weight="bold" /> PDF
                  </button>
                  {!isVoid && (
                    <button onClick={() => voidReceipt(rc.id)} title="Void" data-testid={`receipt-void-${rc.id}`}
                      className="text-[10px] uppercase tracking-wider border border-[#262626] hover:border-[#FF2D2D] hover:text-[#FF2D2D] text-[#a3a3a3] px-2 py-1.5 transition-colors">
                      <Trash size={10} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <CreateReceiptModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => { setCreateOpen(false); load(); }}
      />
    </AdminLayout>
  );
}
